import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEvalRuns } from '@/lib/api/eval-runs';

import { EvalRunStatusBadge } from './eval-run-status-badge';

const PAGE_SIZE = 20;

export function RunsListPage() {
  const [page, setPage] = useState(1);
  const runsQuery = useEvalRuns({ page, pageSize: PAGE_SIZE });

  const result = runsQuery.data;
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Eval runs</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Suite runs triggered from the UI or the CLI.
        </p>
      </div>

      {runsQuery.isPending && <p className="text-sm">Loading…</p>}
      {runsQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load runs: {runsQuery.error.message}
        </p>
      )}

      {result && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>Suite</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Passed</TableHead>
                  <TableHead>Avg tokens</TableHead>
                  <TableHead>Avg latency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.items.length ? (
                  result.items.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell className="text-sm">
                        <Link
                          to="/evals/runs/$runId"
                          params={{ runId: run.id }}
                          className="hover:underline"
                        >
                          {new Date(run.createdAt).toLocaleString()}
                        </Link>
                      </TableCell>
                      <TableCell className="font-medium">{run.suiteName}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{run.source}</Badge>
                      </TableCell>
                      <TableCell>
                        <EvalRunStatusBadge status={run.status} />
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {run.summary
                          ? `${run.summary.passed}/${run.summary.total}`
                          : `${run.taskDone}/${run.taskTotal}`}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">
                        {run.summary
                          ? `${run.summary.avgTokensIn} / ${run.summary.avgTokensOut}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">
                        {run.summary ? `${run.summary.avgDurationMs} ms` : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      No runs yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-muted-foreground)]">{result.total} run(s)</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                Page {page} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => value + 1)}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
